import styles from './index.module.css'

import React, { useState } from 'react'

import type { FilterComponentProps, FilterOperator } from 'types/filter-page'

import { Flex, RadioGroup, Text, TextField } from 'components/ds'

type Mode = 'none' | 'equals' | 'gt' | 'lt' | 'between'

const MODES: { mode: Mode; label: string }[] = [
  { mode: 'none', label: 'No playlists' },
  { mode: 'equals', label: 'Exactly' },
  { mode: 'gt', label: 'More than' },
  { mode: 'lt', label: 'Fewer than' },
  { mode: 'between', label: 'Between' },
]

const deriveMode = (value: any, operator: FilterOperator | undefined): Mode => {
  if (value === undefined || operator === undefined) return 'none'
  if (operator === 'gt') return 'gt'
  if (operator === 'lt') return 'lt'
  if (operator === 'between') return 'between'
  if (value === 0) return 'none'
  return 'equals'
}

const PlaylistCountForm = ({
  value,
  operator,
  onChange,
  onValidityChange,
}: FilterComponentProps) => {
  const [mode, setMode] = useState<Mode>(() => deriveMode(value, operator))
  const [count, setCount] = useState<string>(
    value !== undefined && value !== 0 && !Array.isArray(value)
      ? String(value)
      : '1',
  )
  const [rangeFrom, setRangeFrom] = useState<string>(
    Array.isArray(value) ? String(value[0] ?? '') : '',
  )
  const [rangeTo, setRangeTo] = useState<string>(
    Array.isArray(value) ? String(value[1] ?? '') : '',
  )

  const emitSingle = (raw: string, newMode: Mode) => {
    const parsed = parseInt(raw, 10)
    const op: FilterOperator =
      newMode === 'gt' ? 'gt' : newMode === 'lt' ? 'lt' : 'equals'
    if (!isNaN(parsed) && parsed >= 0) {
      onChange(parsed, op)
      onValidityChange?.(true)
    } else {
      onValidityChange?.(false)
    }
  }

  const emitRange = (from: string, to: string) => {
    const f = parseInt(from, 10)
    const t = parseInt(to, 10)
    const valid = !isNaN(f) && !isNaN(t) && f >= 0 && t >= 0
    onChange(valid ? [f, t] : [from, to], 'between')
    onValidityChange?.(valid)
  }

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode)
    if (newMode === 'none') {
      onChange(0, 'equals')
      onValidityChange?.(true)
    } else if (newMode === 'between') {
      emitRange(rangeFrom, rangeTo)
    } else {
      emitSingle(count, newMode)
    }
  }

  const handleCountChange = (raw: string) => {
    setCount(raw)
    emitSingle(raw, mode)
  }

  const handleRangeChange = (from: string, to: string) => {
    setRangeFrom(from)
    setRangeTo(to)
    emitRange(from, to)
  }

  const singleInput = (
    <TextField.Root
      size="1"
      type="number"
      min="0"
      value={count}
      onChange={e => handleCountChange(e.target.value)}
      placeholder="Number of playlists"
    />
  )

  const rangeInput = (
    <Flex gap="2" align="center">
      <TextField.Root
        size="1"
        type="number"
        min="0"
        value={rangeFrom}
        onChange={e => handleRangeChange(e.target.value, rangeTo)}
        placeholder="From"
      />
      <Text size="2">–</Text>
      <TextField.Root
        size="1"
        type="number"
        min="0"
        value={rangeTo}
        onChange={e => handleRangeChange(rangeFrom, e.target.value)}
        placeholder="To"
      />
    </Flex>
  )

  const getComponent = (m: Mode) => {
    if (m === 'none') return null
    if (m === 'between') return rangeInput
    return singleInput
  }

  return (
    <RadioGroup.Root
      value={mode}
      onValueChange={newMode => handleModeChange(newMode as Mode)}
    >
      <Flex gap="4" direction="column">
        {MODES.map(({ mode: m, label }) => (
          <RadioGroup.Item value={m} key={m} className={styles.item}>
            <Flex gap="2" direction="column">
              <Text>{label}</Text>
              {m === mode && getComponent(m) !== null && (
                <span>{getComponent(m)}</span>
              )}
            </Flex>
          </RadioGroup.Item>
        ))}
      </Flex>
    </RadioGroup.Root>
  )
}

export default PlaylistCountForm
